import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  HStack,
  Text,
  VStack,
  IconButton,
  useMediaQuery,
  Badge,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';

import { useTranslation } from 'react-i18next';
import {
  X,
  ChevronsUp,
  ChevronsDown,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Wind,
  Droplets,
  Thermometer,
  CloudLightning,
  Snowflake,
} from 'lucide-react';
import WeatherHeader from './WeatherHeader';
import WeatherTimeline from './WeatherTimeline';
import WeatherCard from './WeatherCard';
import WeatherWarningDetailModal from './WeatherWarningDetailModal';
import AdSlot from '../../../Misc/AdSlot';
import { WarningSeverity } from '../../../../config/weatherWarnings';
import {
  getUniqueWarningsForLocality,
  getWarningMessage,
} from '../../../../util/WeatherWarnings';
import { findForecastHourIndex } from '../../../../util/passingTimes';

export default function WeatherInfo(props) {
  const { t } = useTranslation();
  const [mainConditions, setMainConditions] = useState({
    weatherCode: null, //estadoCielo value
    weatherDescription: null, //estadoCielo descripcion
    temperature: null, //temperatura
    windDirection: null, //vientoyrachmax 1er array direccion
    windSpeed: null, //vientoyrachmax 1er array velocidad
    windGusts: null, //vientoyrachmax 2do array value
    feelsLike: null, // sensTermica value
    humidity: null, // humedadRelativa value
    precipitation: null, // precipitacion value
    stormProbability: null,
    snowProbability: null,
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedWarning, setSelectedWarning] = useState(null);
  const [selectedWarningPeriod, setSelectedWarningPeriod] = useState(null);

  const {
    data,
    setIndexToRender,
    maxIndex,
    selectedHour,
    setSelectedHour,
    warningsAnalysis,
    hasWarningBanner,
  } = props;

  const {
    isOpen: isWarningModalOpen,
    onOpen: onWarningModalOpen,
    onClose: onWarningModalClose,
  } = useDisclosure();

  // Get warnings for current locality
  const localityWarnings = useMemo(() => {
    if (!warningsAnalysis || !data?.localityName) return null;
    return getUniqueWarningsForLocality(warningsAnalysis, data.localityName);
  }, [warningsAnalysis, data?.localityName]);

  // Organize warnings by hour period for the timeline
  const hourlyWarnings = useMemo(() => {
    if (!warningsAnalysis || !data?.localityName) return {};

    const localityWarningsList =
      warningsAnalysis.warningsByLocality?.[data.localityName];
    if (!localityWarningsList) return {};

    // Group warnings by period
    const warningsByPeriod = {};
    localityWarningsList.forEach(warning => {
      const period = warning.period;
      if (!warningsByPeriod[period]) {
        warningsByPeriod[period] = [];
      }
      warningsByPeriod[period].push(warning);
    });

    return warningsByPeriod;
  }, [warningsAnalysis, data?.localityName]);

  // Handler for warning clicks
  const handleWarningClick = (warning, period) => {
    setSelectedWarning(warning);
    setSelectedWarningPeriod(period);
    onWarningModalOpen();
  };

  // When the locality changes, default the selected hour to the one matching
  // the estimated passing time, so the card shows the forecast for when the
  // traveler will actually be there. The user can still change it manually.
  useEffect(() => {
    if (data?.weatherForecast) {
      const idx = findForecastHourIndex(data.weatherForecast, data.passingTime);
      setSelectedHour(idx);
      const merged = (data.weatherForecast[0]?.hours || []).concat(
        data.weatherForecast[1]?.hours || []
      );
      if (merged[idx]) setMainConditions(merged[idx]);
    }
  }, [data, setSelectedHour]);

  const [isLargerThanMobile] = useMediaQuery('(min-width: 768px)');
  const toggleExpand = () => {
    setIsExpanded(prevState => !prevState);
  };
  return (
    <Box
      borderTopRadius={15}
      position={['absolute']}
      w={['100%', null, null, '30%', '30%']}
      right={[null, null, null, isLargerThanMobile ? 0 : null]}
      zIndex={5}
      maxH={
        isExpanded
          ? hasWarningBanner
            ? 'calc(100dvh - 40px)'
            : '100dvh'
          : [
              '67dvh',
              null,
              null,
              hasWarningBanner ? 'calc(100dvh - 40px)' : '100dvh',
            ]
      }
      overflowY={'auto'}
      bottom={0}
      sx={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {data && (
        <Card w={'100%'} borderTopRadius={15}>
          <Box
            p={3}
            w={'100%'}
            display={'flex'}
            flexDirection={'row'}
            justifyContent={isLargerThanMobile ? 'center' : 'space-between'}
            alignItems={'center'}
          >
            <IconButton
              padding={0}
              background={'transparent'}
              icon={<X size={18} />}
              onClick={() => {
                setIndexToRender(null);
              }}
              display={isLargerThanMobile ? 'none' : 'block'}
              m={4}
            />
            <div>
              <VStack
                w={'100%'}
                gap={0}
                textAlign={'center'}
                justifyContent={'center'}
              >
                <Text textStyle={'poweredBy'}>{t('map.poweredBy')}</Text>
                <Text fontWeight="bold" fontSize="sm">Google Weather</Text>
              </VStack>
            </div>

            <IconButton
              padding={0}
              background={'transparent'}
              icon={isExpanded ? <ChevronsDown size={18} /> : <ChevronsUp size={18} />}
              onClick={toggleExpand}
              display={isLargerThanMobile ? 'none' : 'block'}
              m={4}
            />
          </Box>
          <VStack w={'100%'}>
            <HStack
              w={'90%'}
              display={'flex'}
              flexDirection={'row'}
              alignContent={'center'}
              justifyContent={'space-between'}
            >
              <IconButton
                icon={<ChevronLeft size={20} />}
                minW="44px"
                h="44px"
                onClick={() => {
                  setIndexToRender(prevIndex => {
                    if (prevIndex - 1 >= 0) return prevIndex - 1;
                    else return prevIndex;
                  });
                }}
              />

              <VStack spacing={1}>
                <WeatherHeader
                  localityName={data?.localityName}
                  stateName={data?.localityProvince}
                  weatherCode={mainConditions?.weatherCode}
                  weatherDescription={mainConditions?.weatherDescription}
                  temperature={mainConditions?.temperature}
                  period={mainConditions?.period}
                  passingTime={data?.passingTime}
                />

                {/* Warning indicator badge */}
                {localityWarnings && (
                  <Tooltip
                    label={`Click para ver detalles de las alertas`}
                    hasArrow
                  >
                    <Badge
                      colorScheme={
                        localityWarnings.maxSeverity === WarningSeverity.EXTREME
                          ? 'red'
                          : localityWarnings.maxSeverity ===
                              WarningSeverity.DANGER
                            ? 'orange'
                            : localityWarnings.maxSeverity ===
                                WarningSeverity.WARNING
                              ? 'yellow'
                              : 'blue'
                      }
                      display="flex"
                      alignItems="center"
                      gap={1}
                      px={2}
                      py={1}
                      borderRadius="full"
                      cursor="pointer"
                      onClick={() => {
                        if (localityWarnings.warnings?.[0]) {
                          handleWarningClick(
                            localityWarnings.warnings[0],
                            null
                          );
                        }
                      }}
                      _hover={{
                        transform: 'scale(1.05)',
                      }}
                      transition="transform 0.2s"
                    >
                      <AlertTriangle size={12} />
                      <Text fontSize="xs">
                        {localityWarnings.count} alerta
                        {localityWarnings.count > 1 ? 's' : ''}
                      </Text>
                    </Badge>
                  </Tooltip>
                )}
              </VStack>
              <IconButton
                icon={<ChevronRight size={20} />}
                minW="44px"
                h="44px"
                onClick={() => {
                  setIndexToRender(prevIndex => {
                    if (prevIndex + 1 <= maxIndex) return prevIndex + 1;
                    else return prevIndex;
                  });
                }}
              />
            </HStack>

            <WeatherTimeline
              data={data.weatherForecast}
              setMainConditions={setMainConditions}
              selectedHour={selectedHour}
              setSelectedHour={setSelectedHour}
              hourlyWarnings={hourlyWarnings}
              onWarningClick={handleWarningClick}
            />

            {/* Feels Like highlight — always visible above fold */}
            <Box w={'90%'} mt={3}>
              <Box
                w={'100%'}
                h={'80px'}
                bgColor={'#d9d9d940'}
                borderRadius={8}
                display="flex"
                flexDirection="row"
                overflow="hidden"
              >
                <Box
                  flex={1}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  borderRight="1px solid"
                  borderColor="whiteAlpha.200"
                >
                  <Thermometer size={16} color="#a0a0a0" />
                  <Text textStyle={'weatherCardData'} lineHeight={1} mt={1}>
                    {mainConditions?.temperature}º
                  </Text>
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text textStyle={'weatherCardHeading'} textTransform="uppercase" color="gray.400">
                    {t('map.cards.feelsLike')}
                  </Text>
                  <Text
                    textStyle={'weatherCardData'}
                    lineHeight={1}
                    mt={1}
                    color="#38A169"
                  >
                    {mainConditions?.feelsLike}º
                  </Text>
                </Box>
              </Box>
            </Box>

            {/* Top Ad Slot */}
            <AdSlot placement="weatherInfoTop" />

            <HStack
              w={'90%'}
              mt={2}
              alignContent={'center'}
              justifyContent={'space-evenly'}
              gap={1}
            >
              <WeatherCard
                title={t('map.cards.wind')}
                icon={<Wind size={14} />}
                gustsLabel={t('map.cards.gusts')}
                data={{
                  windSpeed: mainConditions?.windSpeed,
                  windDirection: mainConditions?.windDirection,
                  windGusts: mainConditions?.windGusts,
                }}
              />
              <WeatherCard
                title={t('map.cards.precipitation')}
                icon={<Droplets size={14} />}
                data={`${mainConditions?.precipitation}mm`}
              />
            </HStack>
            <HStack
              w={'90%'}
              alignContent={'center'}
              justifyContent={'space-evenly'}
              gap={1}
            >
              <WeatherCard
                title={t('map.cards.humidity')}
                icon={<Droplets size={14} />}
                data={`${mainConditions?.humidity}%`}
              />
              <WeatherCard
                title={t('map.cards.stormProb')}
                icon={<CloudLightning size={14} />}
                data={`${mainConditions?.stormProbability?.value ?? mainConditions?.stormProbability ?? 0}%`}
              />
            </HStack>

            {/* In-Feed Ad Slot */}
            <AdSlot placement="betweenCards" />

            <HStack
              w={'90%'}
              alignContent={'center'}
              justifyContent={'space-evenly'}
              gap={1}
            >
              <WeatherCard
                title={t('map.cards.snowProb')}
                icon={<Snowflake size={14} />}
                data={`${mainConditions?.snowProbability || 0}%`}
              />
            </HStack>

            {/* Bottom Ad Slot */}
            <AdSlot placement="weatherInfoBottom" />
          </VStack>
        </Card>
      )}

      {/* Warning Detail Modal */}
      <WeatherWarningDetailModal
        isOpen={isWarningModalOpen}
        onClose={onWarningModalClose}
        warning={selectedWarning}
        hourPeriod={selectedWarningPeriod}
        localityName={data?.localityName}
      />
    </Box>
  );
}
